import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { supabase } from '@/lib/supabase/client'

export interface LocationStats {
  locationId: string
  aircraftCount: number
  unitTotal: number
  unitOccupied: number
  occupancyPercent: number
  conflictCount: number
  areaCount: number
  /** All planned aircraft — active + reserved (now and future). */
  plannedAircraftCount: number
  /** Upcoming scheduled events from the flight bridge (aircraft_schedules). */
  scheduledEventCount: number
}

export const locationStatsKeys = {
  all: ['locationStats'] as const,
  byAccount: (organizationId: string) => [...locationStatsKeys.all, organizationId] as const,
}

/**
 * Fetch stats for all accessible locations in a single batch.
 *
 * Delegates to the `get_location_stats` RPC which aggregates counts server-side, so totals
 * are exact (no Supabase 1000-row cap that previously clamped high-volume counts like
 * KAGS Masters-week schedules).
 */
async function fetchAllLocationStats(organizationId: string): Promise<LocationStats[]> {
  const { data, error } = await supabase.rpc('get_location_stats', {
    p_org_id: organizationId,
  })

  if (error) throw error

  return (data ?? []).map((row) => {
    const unitTotal = Number(row.unit_total)
    const unitOccupied = Number(row.unit_occupied)
    return {
      locationId: row.location_id as string,
      aircraftCount: Number(row.aircraft_count),
      unitTotal,
      unitOccupied,
      occupancyPercent: unitTotal > 0 ? Math.round((unitOccupied / unitTotal) * 100) : 0,
      conflictCount: Number(row.conflict_count),
      areaCount: Number(row.area_count),
      plannedAircraftCount: Number(row.planned_aircraft_count),
      scheduledEventCount: Number(row.scheduled_event_count),
    }
  })
}

/**
 * Hook: fetch stats for all locations in the current org.
 * Refreshes every 60 seconds.
 */
export function useAllLocationStats() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: locationStatsKeys.byAccount(organizationId || ''),
    queryFn: () => fetchAllLocationStats(organizationId!),
    enabled: !!organizationId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

/**
 * Helper: extract stats for a specific location from the batch result.
 */
export function getStatsForLocation(
  allStats: LocationStats[] | undefined,
  locationId: string
): LocationStats | undefined {
  return allStats?.find((s) => s.locationId === locationId)
}
